<script>
import { mapActions, mapGetters } from "vuex";
import sizeVue from "@/mixins/size.vue";
import axios from "axios";
import EmptyState from "@/components/EmptyState.vue";
import DatePicker from "@/components/DatePicker.vue";

import examApi from "@/service/exam-api";
const examDatesApi = examApi;

export default {
  mixins: [sizeVue],
  components: { EmptyState, DatePicker },

  data: () => ({
    isLoading: false,
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    tab: 0,

    totalItemsCount: 0,
    page: 1,
    itemsPerPage: 20,

    selectedFaculty: null,
    selectedLanguage: null,
    selectedScience: null,

    isAddModal: false,
    addModalTab: "manual",
    editingQuestion: null,
    isPreviewModal: false,
    previewQuestion: null,

    form: {
      educationFacultyId: null,
      educationLanguageId: null,
      scienceId: null,
      body: "",
      imageUrl: null,
      imageFile: null,
      answers: [
        { value: "", correct: true },
        { value: "", correct: false },
        { value: "", correct: false },
        { value: "", correct: false },
      ],
    },

    uploadFile: null,
    uploadFacultyId: null,
    uploadLanguageId: null,
    uploadScienceId: null,
    uploadResult: null,
    isUploading: false,

    selectedIds: [],
    selectAllMode: false,
    isDeleteModal: false,
    deleteMessage: "",
    deleteAction: null,
    localFaculties: [],
    showFilters: false,
    facultyMenu: false,
    languageMenu: false,
    scienceMenu: false,
    searchFaculty: "",
    searchLanguage: "",
    searchScience: "",
    search: "",
    searchTimer: null,

    // Page-level tab: 0 = Questions, 1 = Exam Dates
    pageTab: 0,

    // Exam Dates
    examDates: [],
    examDatesLoading: false,
    isDateModal: false,
    editingDate: null,
    dateForm: {
      examDate: "",
      startTime: "09:00",
      endTime: "11:00",
      maxParticipants: 50,
      active: true,
    },
    isDeleteDateModal: false,

    // Exam Config
    examConfig: {
      examPassingScore: null,
      maxScore: null,
      examDurationMinutes: null,
      allowedEducationTypeIds: null,
      allowedEducationLanguageIds: null,
      typeLanguageMap: null,
    },
    allEducationTypes: [],
    allEducationLanguages: [],
    examConfigLoading: false,
    eqcOpen: true,
    facultyQuestionLimit: 0,
    facultyLanguageBreakdown: [],
    deleteDateId: null,
  }),

  computed: {
    ...mapGetters("admin-exam-questions", [
      "GET_QUESTIONS", "GET_FACULTIES", "GET_LANGUAGES", "GET_SCIENCES", "GET_STATS",
    ]),

    questions: (vm) => vm.GET_QUESTIONS,
    faculties: (vm) => vm.localFaculties,
    languages: (vm) => vm.GET_LANGUAGES,
    sciences: (vm) => vm.GET_SCIENCES,
    stats: (vm) => vm.GET_STATS,

    isDark() {
      return this.$vuetify.theme.dark;
    },

    displayedQuestionLimit() {
      if (this.selectedLanguage && this.facultyLanguageBreakdown.length) {
        const match = this.facultyLanguageBreakdown.find(lb => lb.languageId === this.selectedLanguage);
        if (match) return match.questionLimit;
      }
      return this.facultyQuestionLimit;
    },

    scorePerAnswer() {
      if (!this.examConfig.maxScore || !this.displayedQuestionLimit) return "—";
      return (this.examConfig.maxScore / this.displayedQuestionLimit).toFixed(2);
    },

    imageFilePreview() {
      if (!this.form.imageFile) return null;
      return URL.createObjectURL(this.form.imageFile);
    },

    totalQuestions() {
      return this.filteredStats.reduce((sum, s) => sum + (s.questionCount || 0), 0);
    },

    allowedTypeSet() {
      if (!this.examConfig.allowedEducationTypeIds) return new Set(this.allEducationTypes.map(t => t.id));
      return new Set(this.examConfig.allowedEducationTypeIds.split(",").map(Number).filter(Boolean));
    },
    allowedLangSet() {
      if (!this.examConfig.allowedEducationLanguageIds) return new Set(this.allEducationLanguages.map(l => l.id));
      return new Set(this.examConfig.allowedEducationLanguageIds.split(",").map(Number).filter(Boolean));
    },
    typeLanguageObj() {
      if (!this.examConfig.typeLanguageMap) return {};
      try { return JSON.parse(this.examConfig.typeLanguageMap); } catch { return {}; }
    },

    filteredFaculties() {
      if (!this.searchFaculty) return this.faculties;
      const s = this.searchFaculty.toLowerCase();
      return this.faculties.filter(f =>
        (f.name || "").toLowerCase().includes(s) ||
        (f.mvdir || "").toLowerCase().includes(s)
      );
    },

    filteredStats() {
      if (!this.faculties.length) return [];
      const ids = new Set(this.faculties.map(f => Number(f.id)));
      return this.stats.filter(s => ids.has(Number(s.facultyId)));
    },

    filteredLanguages() {
      if (!this.searchLanguage) return this.languages;
      const s = this.searchLanguage.toLowerCase();
      return this.languages.filter(l => l.name.toLowerCase().includes(s));
    },

    filteredSciences() {
      if (!this.searchScience) return this.sciences;
      const s = this.searchScience.toLowerCase();
      return this.sciences.filter(sc => sc.name.toLowerCase().includes(s));
    },

    isFiltering() {
      return this.selectedFaculty !== null || this.selectedLanguage !== null || this.selectedScience !== null;
    },

    activeFilterCount() {
      return [this.selectedFaculty, this.selectedLanguage, this.selectedScience].filter(v => v !== null).length;
    },

    allSelected() {
      if (this.selectAllMode) return this.totalItemsCount > 0;
      return this.totalItemsCount > 0 && this.selectedIds.length === this.totalItemsCount;
    },
    someSelected() {
      if (this.selectAllMode) return false;
      return this.selectedIds.length > 0 && !this.allSelected;
    },
    selectionCount() {
      return this.selectAllMode ? this.totalItemsCount : this.selectedIds.length;
    },

    headers() {
      return [
        { text: "", value: "checkbox", width: 50, sortable: false },
        { text: "№", value: "row-number", width: 60, sortable: false },
        { text: this.$t("Question"), value: "body", sortable: false },
        { text: this.$t("Faculty"), value: "educationFacultyName", width: 200, sortable: false },
        { text: this.$t("Fan"), value: "scienceName", width: 150, sortable: false },
        { text: this.$t("Language"), value: "educationLanguageName", width: 100, sortable: false },
        { text: this.$t("Answers"), value: "answerCount", width: 80, sortable: false, align: "center" },
      ];
    },
  },

  watch: {
    page() {
      this.getQuestions();
    },
    selectedFaculty(val) {
      this.page = 1;
      this.selectedIds = [];
      this.selectAllMode = false;
      this.getQuestions();
      if (val) this.loadExamConfig(val);
    },
    selectedLanguage() {
      this.page = 1;
      this.selectedIds = [];
      this.selectAllMode = false;
      this.getQuestions();
    },
    selectedScience() {
      this.page = 1;
      this.selectedIds = [];
      this.selectAllMode = false;
      this.getQuestions();
    },
  },

  async mounted() {
    this.canCreate = await this.hasRole("IMTIHON_SAVOLLARI", "CREATE");
    this.canUpdate = await this.hasRole("IMTIHON_SAVOLLARI", "UPDATE");
    this.canDelete = await this.hasRole("IMTIHON_SAVOLLARI", "DELETE");
    await Promise.all([
      this.loadLocalFaculties(),
      this.INIT_LANGUAGES(),
      this.INIT_SCIENCES(),
      this.INIT_STATS(),
      this.loadVisibilityOptions(),
    ]);
    this.getQuestions();
  },

  methods: {
    facultyLabel(facOrId) {
      const f = facOrId && typeof facOrId === "object"
        ? facOrId
        : this.faculties.find(x => x.id === facOrId);
      if (!f) return "";
      return [f.name, f.mvdir, f.schetType].filter(Boolean).join(" - ");
    },

    ...mapActions("admin-exam-questions", [
      "INIT_QUESTIONS",
      "GET_QUESTION",
      "INIT_LANGUAGES",
      "INIT_SCIENCES",
      "INIT_STATS",
      "ADD_QUESTION",
      "UPDATE_QUESTION",
      "DELETE_QUESTION",
      "DELETE_BY_FACULTY",
      "BULK_DELETE_QUESTIONS",
      "UPLOAD_EXCEL",
    ]),

    async loadVisibilityOptions() {
      try {
        const types = await this.$axios.$get("admin/education-type/all");
        this.allEducationTypes = (types || []).filter(t => t.active !== false);
      } catch { this.allEducationTypes = []; }
      try {
        const langs = await this.$axios.$get("admin/education-language/all");
        this.allEducationLanguages = (langs || []).filter(l => l.active !== false);
      } catch { this.allEducationLanguages = []; }
    },

    toggleTypeId(id) {
      const set = new Set(this.allowedTypeSet);
      const map = { ...this.typeLanguageObj };
      if (set.has(id)) {
        set.delete(id);
        delete map[id];
      } else {
        set.add(id);
        map[id] = "";
      }
      this.examConfig.allowedEducationTypeIds = [...set].join(",");
      this.examConfig.typeLanguageMap = JSON.stringify(map);
    },

    toggleLangForType(typeId, langId) {
      const map = { ...this.typeLanguageObj };
      const current = map[typeId] ? new Set(map[typeId].split(",").map(Number).filter(Boolean)) : new Set();
      current.has(langId) ? current.delete(langId) : current.add(langId);
      map[typeId] = [...current].join(",");
      this.examConfig.typeLanguageMap = JSON.stringify(map);
    },

    langsForType(typeId) {
      const val = this.typeLanguageObj[typeId];
      if (!val) return new Set();
      return new Set(val.split(",").map(Number).filter(Boolean));
    },

    async loadExamConfig(facultyId) {
      if (!facultyId) return;
      try {
        const data = await this.$axios.$get(`admin/exam-config/faculty/${facultyId}`);
        this.examConfig = {
          educationFacultyId: facultyId,
          examPassingScore: data?.examPassingScore || null,
          maxScore: data?.maxScore || null,
          examDurationMinutes: data?.examDurationMinutes || null,
          allowedEducationTypeIds: data?.allowedEducationTypeIds || null,
          allowedEducationLanguageIds: data?.allowedEducationLanguageIds || null,
          typeLanguageMap: data?.typeLanguageMap || null,
        };
      } catch (e) {
        this.examConfig = {
          educationFacultyId: facultyId,
          examPassingScore: null,
          maxScore: null,
          examDurationMinutes: null,
          allowedEducationTypeIds: null,
          allowedEducationLanguageIds: null,
          typeLanguageMap: null,
        };
      }
      // Fetch per-faculty question limit
      try {
        const res = await examApi.get(`admin/exam/sciences/by-faculty/${facultyId}`);
        this.facultyQuestionLimit = res.data?.totalQuestionLimit || 0;
        this.facultyLanguageBreakdown = res.data?.languageBreakdown || [];
      } catch {
        this.facultyQuestionLimit = 0;
        this.facultyLanguageBreakdown = [];
      }
    },

    async saveExamConfig() {
      this.examConfigLoading = true;
      try {
        await this.$axios.$put("admin/exam-config", this.examConfig);
        this.$store.dispatch("SET_SNACKBAR", { text: "Saqlandi", color: "success" });
      } catch (e) {
        console.error("saveExamConfig error:", e);
        this.$store.dispatch("SET_SNACKBAR", { text: "Xatolik", color: "error" });
      } finally {
        this.examConfigLoading = false;
      }
    },

    async loadLocalFaculties() {
      try {
        const [allRaw, priceIdsRaw] = await Promise.all([
          this.$axios.$get("admin/education/faculty/all"),
          this.$axios.$get("admin/faculty/ids-with-price?kurs=1&studyYear=2026-2027"),
        ]);
        const priceSet = new Set(this.$toArray(priceIdsRaw).map(Number));
        this.localFaculties = this.$toArray(allRaw).filter(
          (f) =>
            String(f.schetType).toUpperCase() === "LOCAL" &&
            priceSet.has(Number(f.id))
        );
      } catch (e) {
        console.error("loadLocalFaculties error:", e);
        this.localFaculties = [];
      }
    },

    onStatClick(stat) {
      const isActive = this.selectedFaculty === stat.facultyId && this.selectedLanguage === stat.languageId;
      this.selectedFaculty = isActive ? null : stat.facultyId;
      this.selectedLanguage = isActive ? null : stat.languageId;
    },

    async getQuestions() {
      if (this.isLoading) return;
      this.isLoading = true;

      this.totalItemsCount = await this.INIT_QUESTIONS({
        facultyId: this.selectedFaculty,
        languageId: this.selectedLanguage,
        scienceId: this.selectedScience,
        search: this.search,
        page: this.page - 1,
        size: this.itemsPerPage,
      });

      this.isLoading = false;
    },

    // Search by question content (debounced live search + immediate on enter/clear).
    applySearch() {
      this.page = 1;
      this.getQuestions();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applySearch(), 350);
    },

    onAddQuestion() {
      this.editingQuestion = null;
      this.form = {
        educationFacultyId: this.selectedFaculty,
        educationLanguageId: this.selectedLanguage,
        scienceId: this.selectedScience,
        body: "",
        imageUrl: null,
        imageFile: null,
        answers: [
          { value: "", correct: true },
          { value: "", correct: false },
          { value: "", correct: false },
          { value: "", correct: false },
        ],
      };
      this.addModalTab = "manual";
      this.isAddModal = true;
    },

    // Read-only preview — opened by clicking a question row.
    onPreviewQuestion(item) {
      this.previewQuestion = item;
      this.isPreviewModal = true;
    },
    isRealImage(url) {
      return !!url && /\.(png|jpe?g|gif|webp)$/i.test(url);
    },
    previewImageSrc(url) {
      return "https://api.usat.uz/qabul" + url;
    },
    onEditFromPreview() {
      const item = this.previewQuestion;
      this.isPreviewModal = false;
      if (item) this.onEditQuestion(item);
    },

    async onEditQuestion(item) {
      const question = await this.GET_QUESTION({ id: item.id });
      if (!question) return;

      this.editingQuestion = question;
      this.form = {
        educationFacultyId: question.educationFacultyId,
        educationLanguageId: question.educationLanguageId,
        scienceId: question.scienceId || null,
        body: question.body,
        imageUrl: question.imageUrl || null,
        imageFile: null,
        answers: question.answers.map((a) => ({
          value: a.value,
          correct: a.correct,
        })),
      };

      while (this.form.answers.length < 4) {
        this.form.answers.push({ value: "", correct: false });
      }

      this.isAddModal = true;
    },

    async onSaveQuestion() {
      if (!this.form.educationFacultyId
          || !this.form.educationLanguageId || !this.form.scienceId || !this.form.body.trim()) return;

      const filteredAnswers = this.form.answers.filter((a) => a.value.trim());
      if (filteredAnswers.length < 2) return;

      // Upload image file if selected
      let imageUrl = this.form.imageUrl;
      if (this.form.imageFile) {
        try {
          const fd = new FormData();
          fd.append("file", this.form.imageFile);
          const { data } = await examApi.post("file/upload/exam-images", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          imageUrl = "/api/v1/file/exam-images/" + data.storedFileName;
        } catch (e) {
          console.error("Image upload failed:", e);
          this.$store.dispatch("SET_SNACKBAR", { text: "Rasm yuklanmadi", color: "error" });
          return;
        }
      }

      const payload = {
        educationFacultyId: this.form.educationFacultyId,
        educationLanguageId: this.form.educationLanguageId,
        scienceId: this.form.scienceId,
        body: this.form.body.trim(),
        imageUrl: imageUrl || null,
        answers: filteredAnswers,
      };

      if (this.editingQuestion) {
        await this.UPDATE_QUESTION({ id: this.editingQuestion.id, question: payload });
      } else {
        await this.ADD_QUESTION(payload);
      }

      this.isAddModal = false;
      this.getQuestions();
      this.INIT_STATS();
    },

    toggleSelectAll() {
      if (this.selectAllMode || this.allSelected) {
        this.selectAllMode = false;
        this.selectedIds = [];
        return;
      }
      // No fetch, no list mutation — flip a flag and let the bulk-delete
      // endpoint do the by-filter work. Avoids the multi-second freeze that
      // happened when we previously fetched every question just to read IDs.
      this.selectAllMode = true;
      this.selectedIds = [];
    },

    toggleSelect(id) {
      // First per-row toggle while in select-all mode collapses back to
      // explicit ID selection so the user can deselect a single row.
      if (this.selectAllMode) {
        this.selectAllMode = false;
        this.selectedIds = this.questions.map((q) => q.id).filter((qid) => qid !== id);
        return;
      }
      const idx = this.selectedIds.indexOf(id);
      if (idx >= 0) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(id);
      }
    },

    onBulkDelete() {
      if (!this.selectAllMode && !this.selectedIds.length) return;
      this.deleteMessage = `${this.selectionCount} ta savolni o'chirmoqchimisiz?`;
      this.deleteAction = async () => {
        if (this.selectAllMode) {
          await this.DELETE_BY_FACULTY({
            facultyId: this.selectedFaculty,
            languageId: this.selectedLanguage,
            scienceId: this.selectedScience,
          });
        } else {
          await this.BULK_DELETE_QUESTIONS({ ids: this.selectedIds });
        }
        this.selectedIds = [];
        this.selectAllMode = false;
        this.getQuestions();
        this.INIT_STATS();
      };
      this.isDeleteModal = true;
    },

    onDeleteQuestion(item) {
      this.deleteMessage = this.$t("Are you sure you want to delete this question?");
      this.deleteAction = async () => {
        await this.DELETE_QUESTION({ id: item.id });
        this.getQuestions();
        this.INIT_STATS();
      };
      this.isDeleteModal = true;
    },

    async confirmDelete() {
      if (this.deleteAction) await this.deleteAction();
      this.isDeleteModal = false;
      this.deleteAction = null;
    },

    setCorrectAnswer(index) {
      this.form.answers.forEach((a, i) => {
        a.correct = i === index;
      });
    },

    onOpenUpload() {
      this.uploadFile = null;
      this.uploadFacultyId = this.selectedFaculty;
      this.uploadLanguageId = this.selectedLanguage;
      this.uploadScienceId = this.selectedScience;
      this.uploadResult = null;
      this.addModalTab = "excel";
      this.isAddModal = true;
    },

    async onUploadExcel() {
      if (!this.uploadFile || !this.uploadFacultyId || !this.uploadLanguageId) return;

      this.isUploading = true;
      this.uploadResult = await this.UPLOAD_EXCEL({
        file: this.uploadFile,
        facultyId: this.uploadFacultyId,
        languageId: this.uploadLanguageId,
        scienceId: this.uploadScienceId,
      });
      this.isUploading = false;

      if (this.uploadResult) {
        this.getQuestions();
        this.INIT_STATS();
      }
    },

    onDeleteByFaculty() {
      if (!this.selectedFaculty) return;

      const faculty = this.faculties.find((f) => f.id === this.selectedFaculty);
      const name = faculty ? faculty.name : "";

      this.deleteMessage = `"${name}" yo'nalishi bo'yicha barcha savollarni o'chirmoqchimisiz?`;
      this.deleteAction = async () => {
        await this.DELETE_BY_FACULTY({
          facultyId: this.selectedFaculty,
          languageId: this.selectedLanguage,
          scienceId: this.selectedScience,
        });
        this.getQuestions();
        this.INIT_STATS();
      };
      this.isDeleteModal = true;
    },

    onFileChange(file) {
      this.uploadFile = file;
    },

    formatNumber(n) {
      if (n == null) return "0";
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },

    truncate(text, len = 100) {
      if (!text) return "";
      return text.length > len ? text.substring(0, len) + "..." : text;
    },

    // === Exam Dates Methods ===
    async loadExamDates() {
      this.examDatesLoading = true;
      try {
        const { data } = await examDatesApi.get("admin/exam/allowed-dates");
        this.examDates = data;
      } catch (e) {
        console.error("Failed to load exam dates:", e);
      } finally {
        this.examDatesLoading = false;
      }
    },

    openAddDate() {
      this.editingDate = null;
      this.dateForm = {
        examDate: "",
        startTime: "09:00",
        endTime: "11:00",
        maxParticipants: 50,
        active: true,
      };
      this.isDateModal = true;
    },

    openEditDate(item) {
      this.editingDate = item;
      this.dateForm = {
        examDate: item.examDate,
        startTime: item.startTime,
        endTime: item.endTime,
        maxParticipants: item.availableSlots || 50,
        active: true,
      };
      this.isDateModal = true;
    },

    async saveDate() {
      try {
        if (this.editingDate) {
          await examDatesApi.put(`admin/exam/allowed-dates/${this.editingDate.id}`, this.dateForm);
        } else {
          await examDatesApi.post("admin/exam/allowed-dates", this.dateForm);
        }
        this.isDateModal = false;
        this.loadExamDates();
      } catch (e) {
        console.error("Failed to save exam date:", e);
      }
    },

    confirmDeleteDate(id) {
      this.deleteDateId = id;
      this.isDeleteDateModal = true;
    },

    async deleteDate() {
      try {
        await examDatesApi.delete(`admin/exam/allowed-dates/${this.deleteDateId}`);
        this.isDeleteDateModal = false;
        this.loadExamDates();
      } catch (e) {
        console.error("Failed to delete exam date:", e);
      }
    },

    formatExamDate(dateStr) {
      if (!dateStr) return "";
      try {
        const d = new Date(dateStr + "T00:00:00");
        return d.toLocaleDateString("uz-UZ", { day: "2-digit", month: "2-digit", year: "numeric" });
      } catch {
        return dateStr;
      }
    },


    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },
  },
};
</script>

<template>
  <div class="eq" :class="{ 'eq--dark': isDark }">

    <!-- Page-Level Tabs -->
    <div class="eq-page-tabs">
      <button
        class="eq-page-tab"
        :class="{ 'eq-page-tab--active': pageTab === 0 }"
        @click="pageTab = 0"
      >
        <v-icon size="18" class="mr-1">mdi-help-circle-outline</v-icon>
        Savollar
      </button>
      <button
        class="eq-page-tab"
        :class="{ 'eq-page-tab--active': pageTab === 1 }"
        @click="pageTab = 1; loadExamDates()"
      >
        <v-icon size="18" class="mr-1">mdi-calendar-clock</v-icon>
        Offlayn imtihon sanalari
      </button>
    </div>

    <!-- TAB 0: Questions (existing content) -->
    <template v-if="pageTab === 0">

    <!-- Add/Edit Question Dialog -->
    <!-- ── Read-only question preview (opened by clicking a row) ── -->
    <v-dialog v-model="isPreviewModal" max-width="680" content-class="eq-dialog">
      <v-card v-if="previewQuestion" class="eq-dialog-card">
        <div class="eq-dialog-header">
          <h3 class="eq-dialog-title">{{ $t("Question") }}</h3>
          <button class="eq-close-btn" @click="isPreviewModal = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="eq-dialog-body">
          <div class="eq-preview__meta">
            <span class="eq-chip">{{ previewQuestion.educationFacultyName }}</span>
            <span v-if="previewQuestion.scienceName" class="eq-chip eq-chip--science">{{ previewQuestion.scienceName }}</span>
            <span class="eq-chip eq-chip--lang">{{ previewQuestion.educationLanguageName }}</span>
          </div>

          <div class="eq-preview__body">{{ previewQuestion.body }}</div>

          <img
            v-if="isRealImage(previewQuestion.imageUrl)"
            :src="previewImageSrc(previewQuestion.imageUrl)"
            alt="question image"
            class="eq-preview__img"
          />

          <div class="eq-preview__answers">
            <div
              v-for="(a, i) in (previewQuestion.answers || [])"
              :key="i"
              class="eq-preview__answer"
              :class="{ 'is-correct': a.correct }"
            >
              <span class="eq-preview__letter">{{ ['A','B','C','D','E','F'][i] || (i + 1) }}</span>
              <span class="eq-preview__value">{{ a.value }}</span>
              <v-icon v-if="a.correct" size="18" color="#148b4b" class="eq-preview__tick">mdi-check-circle</v-icon>
            </div>
          </div>
        </div>

        <div class="eq-dialog-footer">
          <button class="eq-btn eq-btn--ghost" @click="isPreviewModal = false"><v-icon size="16" class="mr-1">mdi-close</v-icon> {{ $t("Close") }}</button>
          <button v-if="canUpdate" class="eq-btn eq-btn--primary" @click="onEditFromPreview"><v-icon size="16" class="mr-1">mdi-pencil</v-icon> {{ $t("Edit") }}</button>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isAddModal" max-width="720" persistent content-class="eq-dialog">
      <v-card class="eq-dialog-card">
        <div class="eq-dialog-header">
          <h3 class="eq-dialog-title">
            {{ editingQuestion ? $t("Edit Question") : $t("Add Question") }}
          </h3>
          <button class="eq-close-btn" @click="isAddModal = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <!-- Tab Bar (hidden when editing) -->
        <div v-if="!editingQuestion" class="eq-seg-wrap">
          <div class="eq-seg">
            <button class="eq-seg__btn" :class="{ 'is-active': addModalTab === 'manual' }" @click="addModalTab = 'manual'"><v-icon size="16" class="mr-1">mdi-plus</v-icon> Manual</button>
            <button class="eq-seg__btn" :class="{ 'is-active': addModalTab === 'excel' }" @click="addModalTab = 'excel'"><img src="/icons/excel-icon.svg" alt="Excel" class="eq-seg__icon"> Excel</button>
            <span class="eq-seg__fill" :style="{ transform: addModalTab === 'excel' ? 'translateX(100%)' : 'translateX(0)' }"></span>
          </div>
        </div>

        <!-- Manual Tab -->
        <div v-if="addModalTab === 'manual' || editingQuestion" class="eq-dialog-body">
          <div class="eq-form-row--two">
            <v-autocomplete
              v-model="form.educationFacultyId"
              :items="faculties"
              :item-text="facultyLabel"
              item-value="id"
              :label="$t('Faculty')"
              outlined
              dense
              hide-details
              :menu-props="{ bottom: true, offsetY: true, contentClass: 'eq-faculty-menu' }"
            />
            <v-select
              v-model="form.educationLanguageId"
              :items="languages"
              item-text="name"
              item-value="id"
              :label="$t('Language')"
              outlined
              dense
              hide-details
              :menu-props="{ bottom: true, offsetY: true, contentClass: 'eq-faculty-menu' }"
            />
          </div>
          <div class="eq-form-row">
            <v-autocomplete
              v-model="form.scienceId"
              :items="sciences"
              item-text="name"
              item-value="id"
              :label="$t('Fan')"
              outlined
              dense
              hide-details
              :menu-props="{ bottom: true, offsetY: true, contentClass: 'eq-faculty-menu' }"
            />
          </div>
          <v-textarea
            v-model="form.body"
            :label="$t('Question text')"
            outlined
            dense
            rows="3"
            hide-details
            class="mb-4"
          />

          <!-- Question Image -->
          <div class="eq-image-section mb-5">
            <div v-if="form.imageUrl && !form.imageFile" class="eq-image-preview">
              <img :src="'https://api.usat.uz/qabul' + form.imageUrl" alt="question image" class="eq-image-preview__img" />
              <button type="button" class="eq-image-preview__remove" @click="form.imageUrl = null">
                <v-icon size="16" color="white">mdi-close</v-icon>
              </button>
            </div>
            <div v-if="form.imageFile" class="eq-image-preview">
              <img :src="imageFilePreview" alt="new image" class="eq-image-preview__img" />
              <button type="button" class="eq-image-preview__remove" @click="form.imageFile = null">
                <v-icon size="16" color="white">mdi-close</v-icon>
              </button>
            </div>
            <v-file-input
              v-if="!form.imageUrl && !form.imageFile"
              @change="(f) => form.imageFile = f"
              accept="image/*"
              :label="$t('Rasm biriktirish (ixtiyoriy)')"
              outlined
              dense
              hide-details
              prepend-icon="mdi-image-plus"
            />
          </div>

          <div class="eq-answers-label">{{ $t("Answer options") }}</div>
          <div
            v-for="(answer, index) in form.answers"
            :key="index"
            class="eq-answer-row"
            :class="{ 'eq-answer-row--correct': answer.correct }"
          >
            <button
              class="eq-answer-radio"
              :class="{ 'eq-answer-radio--active': answer.correct }"
              @click="setCorrectAnswer(index)"
            >
              <v-icon v-if="answer.correct" size="16" color="black">mdi-check</v-icon>
            </button>
            <div class="eq-answer-letter">{{ String.fromCharCode(65 + index) }}</div>
            <v-text-field
              v-model="answer.value"
              outlined
              dense
              hide-details
              :placeholder="`${$t('Answer options')} ${String.fromCharCode(65 + index)}`"
              class="eq-answer-input"
            />
          </div>
          <div class="eq-answer-hint">
            {{ $t("Select the radio button next to the correct answer") }}
          </div>
        </div>

        <!-- Excel Tab -->
        <div v-if="addModalTab === 'excel' && !editingQuestion" class="eq-dialog-body">
          <div class="eq-form-row--two">
            <v-autocomplete
              v-model="uploadFacultyId"
              :items="faculties"
              :item-text="facultyLabel"
              item-value="id"
              :label="$t('Faculty')"
              outlined
              dense
              hide-details
              :menu-props="{ bottom: true, offsetY: true, contentClass: 'eq-faculty-menu' }"
            />
            <v-select
              v-model="uploadLanguageId"
              :items="languages"
              item-text="name"
              item-value="id"
              :label="$t('Language')"
              outlined
              dense
              hide-details
              :menu-props="{ bottom: true, offsetY: true, contentClass: 'eq-faculty-menu' }"
            />
          </div>
          <div class="eq-form-row">
            <v-autocomplete
              v-model="uploadScienceId"
              :items="sciences"
              item-text="name"
              item-value="id"
              :label="$t('Fan')"
              outlined
              dense
              hide-details
              :menu-props="{ bottom: true, offsetY: true, contentClass: 'eq-faculty-menu' }"
            />
          </div>

          <v-file-input
            @change="onFileChange"
            accept=".xlsx,.xls"
            :label="$t('Select Excel file (.xlsx)')"
            outlined
            dense
            hide-details
            prepend-icon="mdi-file-excel"
            class="mb-4"
          />

          <div class="eq-format-hint">
            <div class="eq-format-hint__title">{{ $t("Excel format") }}:</div>
            <div class="eq-format-hint__body">
              <span>A: Savol</span> <span class="eq-format-sep">|</span>
              <span>B: Rasm</span> <span class="eq-format-sep">|</span>
              <span>C: To'g'ri javob</span> <span class="eq-format-sep">|</span>
              <span>D-F: Variantlar</span>
            </div>
            <div class="eq-format-hint__note">Birinchi qator sarlavha (o'tkazib yuboriladi)</div>
          </div>

          <!-- Upload result -->
          <div v-if="uploadResult" class="eq-upload-result" :class="uploadResult.created > 0 ? 'eq-upload-result--success' : 'eq-upload-result--warn'">
            <v-icon size="20" :color="uploadResult.created > 0 ? '#148b4b' : '#b88300'" class="mr-2">
              {{ uploadResult.created > 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <div>
              <strong>{{ uploadResult.created }}</strong> ta savol yaratildi,
              <strong>{{ uploadResult.skipped }}</strong> ta o'tkazib yuborildi
              <div v-if="uploadResult.errors && uploadResult.errors.length" class="eq-upload-errors">
                <div v-for="(err, i) in uploadResult.errors.slice(0, 5)" :key="i">{{ err }}</div>
                <div v-if="uploadResult.errors.length > 5">...va yana {{ uploadResult.errors.length - 5 }} ta xato</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="eq-dialog-footer">
          <button class="eq-btn eq-btn--ghost" @click="isAddModal = false"><v-icon size="16" class="mr-1">mdi-close</v-icon> {{ $t("Cancel") }}</button>
          <button
            v-if="addModalTab === 'manual' || editingQuestion"
            class="eq-btn eq-btn--primary"
            @click="onSaveQuestion"
            :disabled="!form.body || !form.educationFacultyId || !form.educationLanguageId || !form.scienceId"
          >
            <v-icon size="16" class="mr-1">mdi-check</v-icon>
            {{ $t("Save") }}
          </button>
          <button
            v-else
            class="eq-btn eq-btn--excel"
            @click="onUploadExcel"
            :disabled="!uploadFile || !uploadFacultyId || !uploadLanguageId || !uploadScienceId || isUploading"
          >
            <v-progress-circular v-if="isUploading" indeterminate size="16" width="2" class="mr-1" />
            <v-icon v-else size="16" class="mr-1" color="white">mdi-upload</v-icon>
            {{ $t("Upload") }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Modal -->
    <v-dialog v-model="isDeleteModal" max-width="440" content-class="eq-dialog">
      <v-card class="eq-dialog-card">
        <div class="eq-dialog-header">
          <h3 class="eq-dialog-title">O'chirish</h3>
        </div>
        <div class="eq-dialog-body" style="padding: 20px 24px;">
          <p style="margin: 0; color: #344054; font-size: 14px;">{{ deleteMessage }}</p>
        </div>
        <div class="eq-dialog-footer">
          <button class="eq-btn eq-btn--ghost" @click="isDeleteModal = false">Bekor qilish</button>
          <button class="eq-btn eq-btn--delete-confirm" @click="confirmDelete">Ha, o'chirish</button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Faculty Stats Grid -->
    <div class="eq-stats-grid">
      <div
        v-for="stat in filteredStats"
        :key="stat.facultyId + '-' + stat.languageId"
        class="eq-stat-card"
        :class="{ 'eq-stat-card--active': selectedFaculty === stat.facultyId && selectedLanguage === stat.languageId }"
        @click="onStatClick(stat)"
        :style="{ '--accent': '#2a2824' }"
      >
        <div class="eq-stat-card__top">
          <div class="eq-stat-card__title">{{ facultyLabel(stat.facultyId) || stat.facultyName }}</div>
          <span class="eq-stat-card__lang">{{ stat.languageName }}</span>
        </div>
        <div class="eq-stat-card__count">{{ formatNumber(stat.questionCount) }}</div>
      </div>
    </div>

    <!-- Exam Config (per-faculty) -->
    <transition name="slide-fade">
      <div v-if="selectedFaculty" class="eqc">
        <div class="eqc__top" @click="eqcOpen = !eqcOpen">
          <div class="eqc__title">
            <v-icon size="18" class="eqc__title-icon">mdi-cog-outline</v-icon>
            Imtihon sozlamalari
          </div>
          <v-icon size="20" class="eqc__chevron" :class="{ 'eqc__chevron--open': eqcOpen }">mdi-chevron-down</v-icon>
        </div>

        <template v-if="eqcOpen">
          <div class="eqc__grid">
            <div class="eqc__item eqc__item--edit" style="--item-accent: #3b82f6;">
              <span class="eqc__label">Umumiy ball</span>
              <input v-model.number="examConfig.maxScore" type="number" class="eqc__input" />
              <span class="eqc__accent-bar"></span>
            </div>
            <div class="eqc__item eqc__item--edit" style="--item-accent: #10b981;">
              <span class="eqc__label">O'tish bali</span>
              <input v-model.number="examConfig.examPassingScore" type="number" step="0.1" class="eqc__input" />
              <span class="eqc__accent-bar"></span>
            </div>
            <div class="eqc__item eqc__item--edit" style="--item-accent: #8b5cf6;">
              <span class="eqc__label">Imtihon vaqti</span>
              <div class="eqc__input-wrap">
                <input v-model.number="examConfig.examDurationMinutes" type="number" class="eqc__input" />
                <span class="eqc__unit">min</span>
              </div>
              <span class="eqc__accent-bar"></span>
            </div>
            <div class="eqc__item eqc__item--readonly" style="--item-accent: #f59e0b;">
              <span class="eqc__label">Savollar soni</span>
              <span class="eqc__value">{{ displayedQuestionLimit }}</span>
              <span class="eqc__accent-bar"></span>
            </div>
            <div class="eqc__item eqc__item--readonly" style="--item-accent: #ef4444;">
              <span class="eqc__label">Har bir javobga</span>
              <span class="eqc__value">{{ scorePerAnswer }} <small>ball</small></span>
              <span class="eqc__accent-bar"></span>
            </div>
          </div>

          <div class="eqc__visibility">
            <div class="eqc__vis-header">
              <span class="eqc__vis-title">Qabulda ko'rsatish</span>
              <span class="eqc__vis-badge">{{ allowedTypeSet.size }} ta'lim turi</span>
            </div>
            <div class="eqc__type-grid">
              <div v-for="t in allEducationTypes" :key="'tg'+t.id"
                class="eqc__type-card"
                :class="{ 'eqc__type-card--on': allowedTypeSet.has(t.id) }">
                <label class="eqc__type-head" @click="toggleTypeId(t.id)">
                  <span class="eqc__tick-box" :class="{ 'eqc__tick-box--on': allowedTypeSet.has(t.id) }">
                    <v-icon v-if="allowedTypeSet.has(t.id)" size="13" color="#fff">mdi-check</v-icon>
                  </span>
                  <span class="eqc__type-name">{{ t.name }}</span>
                </label>
                <transition name="eqc-expand">
                  <div v-if="allowedTypeSet.has(t.id)" class="eqc__lang-list">
                    <label v-for="l in allEducationLanguages" :key="'tl'+t.id+'-'+l.id"
                      class="eqc__lang-chip"
                      :class="{ 'eqc__lang-chip--on': langsForType(t.id).has(l.id) }"
                      @click="toggleLangForType(t.id, l.id)">
                      <v-icon v-if="langsForType(t.id).has(l.id)" size="12" class="eqc__lang-check">mdi-check</v-icon>
                      {{ l.name }}
                    </label>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <div class="eqc__footer">
            <button class="eqc__cancel" @click="loadExamConfig(selectedFaculty)">Bekor qilish</button>
            <button class="eqc__save" :disabled="examConfigLoading" @click="saveExamConfig">
              <v-icon size="14" class="mr-1">mdi-check</v-icon>
              {{ examConfigLoading ? '...' : 'Saqlash' }}
            </button>
          </div>
        </template>
      </div>
    </transition>

    <!-- Main Table Card -->
    <div class="eq-list">
      <!-- Toolbar -->
      <div class="eq-toolbar">
        <v-text-field
          v-model="search"
          :placeholder="$t('Savol matni...')"
          append-icon="mdi-magnify"
          dense
          hide-details
          outlined
          clearable
          class="eq-search"
          @input="onSearchInput"
          @keyup.enter="applySearch"
          @click:append="applySearch"
          @click:clear="search = ''; applySearch()"
        />
        <div class="eq-toolbar__spacer"></div>

        <v-btn
          class="filter-btn"
          :class="{ 'filter-btn--active': showFilters || isFiltering }"
          elevation="0"
          rounded
          @click="showFilters = !showFilters"
        >
          <img src="../../assets/filter.svg" alt="Filter">
          <span v-if="activeFilterCount" class="eq-filter-badge">{{ activeFilterCount }}</span>
        </v-btn>

        <v-btn
          v-if="canCreate"
          class="excel-btn"
          elevation="0"
          rounded
          @click="onOpenUpload"
        >
          <img src="/icons/excel-icon.svg" alt="Excel">
        </v-btn>

        <v-btn
          v-if="canCreate"
          class="add-btn"
          elevation="0"
          rounded
          color="#ffd451"
          @click="onAddQuestion"
        >
          <v-icon size="20" color="#1a1500">mdi-plus</v-icon>
        </v-btn>


        <v-btn
          v-if="selectedIds.length === 1 && canUpdate"
          class="edit-btn"
          elevation="0"
          rounded
          @click="onEditQuestion({ id: selectedIds[0] })"
        >
          <v-icon size="18" color="#fff">mdi-pencil-outline</v-icon>
        </v-btn>

        <v-btn
          v-if="(selectAllMode || selectedIds.length) && canDelete"
          class="delete-btn"
          elevation="0"
          rounded
          @click="onBulkDelete"
        >
          <v-icon size="22" color="#fff">mdi-trash-can</v-icon>
          <span class="delete-badge">{{ selectionCount }}</span>
        </v-btn>

      </div>

      <!-- Filter Panel -->
      <v-fade-transition>
        <v-container v-if="showFilters" fluid>
          <div style="display: flex; flex-grow: 1; gap: 16px; flex-wrap: wrap;">
            <v-menu
              v-model="facultyMenu"
              :close-on-content-click="false"
              offset-y
              bottom
              allow-overflow
              transition="scale-transition primary-menu"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn class="button-primary" v-bind="attrs" v-on="on">
                  {{ selectedFaculty ? faculties.find(f => f.id === selectedFaculty)?.name || $t('Yo\'nalish') : $t('Yo\'nalish') }}
                </v-btn>
              </template>

              <v-card
                :style="{
                  maxWidth: '274px',
                  maxHeight: '280px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: $vuetify.theme.dark ? '#1a1b23!important' : '#FFF!important',
                }"
              >
                <v-card-title>
                  <v-text-field
                    v-model="searchFaculty"
                    :label="$t('Qidiruv')"
                    clearable
                    hide-details
                    dense
                  />
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text style="overflow-y: auto; flex: 1;">
                  <div style="display: flex; flex-wrap: wrap;">
                    <div>
                      <v-checkbox
                        v-for="item in filteredFaculties"
                        :key="item.id"
                        :label="facultyLabel(item)"
                        :input-value="selectedFaculty === item.id"
                        @change="selectedFaculty = $event ? item.id : null"

                        hide-details
                        dense
                        class="mb-2 mt-0"
                      />
                    </div>
                  </div>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="facultyMenu = false">{{ $t("Close") }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>

            <v-menu
              v-model="languageMenu"
              :close-on-content-click="false"
              offset-y
              bottom
              allow-overflow
              transition="scale-transition primary-menu"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn class="button-primary" v-bind="attrs" v-on="on">
                  {{ selectedLanguage ? languages.find(l => l.id === selectedLanguage)?.name || $t('Til') : $t('Til') }}
                </v-btn>
              </template>

              <v-card
                :style="{
                  maxWidth: '274px',
                  backgroundColor: $vuetify.theme.dark ? '#1a1b23!important' : '#FFF!important',
                  height: 'max-content',
                }"
              >
                <v-card-title>
                  <v-text-field
                    v-model="searchLanguage"
                    :label="$t('Qidiruv')"
                    clearable
                    hide-details
                    dense
                  />
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text style="overflow-y: auto; flex: 1;">
                  <div style="display: flex; flex-wrap: wrap;">
                    <div>
                      <v-checkbox
                        v-for="item in filteredLanguages"
                        :key="item.id"
                        :label="item.name"
                        :input-value="selectedLanguage === item.id"
                        @change="selectedLanguage = $event ? item.id : null"

                        hide-details
                        dense
                        class="mb-2 mt-0"
                      />
                    </div>
                  </div>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="languageMenu = false">{{ $t("Close") }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>

            <v-menu
              v-model="scienceMenu"
              :close-on-content-click="false"
              offset-y
              bottom
              allow-overflow
              transition="scale-transition primary-menu"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn class="button-primary" v-bind="attrs" v-on="on">
                  {{ selectedScience ? sciences.find(s => s.id === selectedScience)?.name || $t('Fan') : $t('Fan') }}
                </v-btn>
              </template>

              <v-card
                :style="{
                  maxWidth: '274px',
                  maxHeight: '280px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: $vuetify.theme.dark ? '#1a1b23!important' : '#FFF!important',
                }"
              >
                <v-card-title>
                  <v-text-field
                    v-model="searchScience"
                    :label="$t('Qidiruv')"
                    clearable
                    hide-details
                    dense
                  />
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text style="overflow-y: auto; flex: 1;">
                  <div style="display: flex; flex-wrap: wrap;">
                    <div>
                      <v-checkbox
                        v-for="item in filteredSciences"
                        :key="item.id"
                        :label="item.name"
                        :input-value="selectedScience === item.id"
                        @change="selectedScience = $event ? item.id : null"
                        hide-details
                        dense
                        class="mb-2 mt-0"
                      />
                    </div>
                  </div>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="scienceMenu = false">{{ $t("Close") }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </div>
        </v-container>
      </v-fade-transition>

      <!-- Questions Table -->
      <v-data-table
        :mobile-breakpoint="0"
        :loading="isLoading"
        :headers="headers"
        :items="questions"
        :server-items-length="totalItemsCount"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        hide-default-footer
        hide-default-header
        class="eq-table"
      >
        <template v-slot:header="{ props: headerProps }">
          <thead class="eq-thead">
            <tr>
              <th v-for="h in headerProps.headers" :key="h.value" :style="{ width: h.width ? h.width + 'px' : 'auto' }" :class="h.align === 'center' ? 'text-center' : ''">
                <v-checkbox
                  v-if="h.value === 'checkbox'"
                  dense
                  hide-details
                  :input-value="allSelected"
                  :indeterminate="someSelected"
                  @change="toggleSelectAll"

                />
                <template v-else>{{ h.text }}</template>
              </th>
            </tr>
          </thead>
        </template>

        <template v-slot:body="{ items }">
          <tbody>
            <tr
              v-for="(item, index) in items"
              :key="item.id"
              class="eq-row eq-row--clickable"
              :class="{ 'eq-row--selected': selectAllMode || selectedIds.includes(item.id) }"
              @click="onPreviewQuestion(item)"
            >
              <td class="eq-td-check" @click.stop>
                <v-checkbox
                  dense
                  hide-details
                  :input-value="selectAllMode || selectedIds.includes(item.id)"
                  @change="toggleSelect(item.id)"

                />
              </td>
              <td class="eq-row__num">{{ (page - 1) * itemsPerPage + index + 1 }}</td>
              <td>
                <div class="eq-row__body">{{ truncate(item.body, 120) }}</div>
              </td>
              <td>
                <span class="eq-chip">{{ item.educationFacultyName }}</span>
              </td>
              <td>
                <span v-if="item.scienceName" class="eq-chip eq-chip--science">{{ item.scienceName }}</span>
                <span v-else class="eq-chip eq-chip--empty">—</span>
              </td>
              <td>
                <span class="eq-chip eq-chip--lang">{{ item.educationLanguageName }}</span>
              </td>
              <td class="text-center">
                <span class="eq-badge">{{ item.answers ? item.answers.length : 0 }}</span>
              </td>
            </tr>
            <tr v-if="!items.length && !isLoading">
              <td :colspan="headers.length">
                <empty-state :title="$t('No results found')" />
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>

      <!-- Pagination -->
      <div class="eq-pagination" v-if="totalItemsCount > itemsPerPage">
        <v-pagination
          v-model="page"
          :length="Math.ceil(totalItemsCount / itemsPerPage)"
          :total-visible="7"
        />
      </div>
    </div>

    </template>

    <!-- TAB 1: Exam Dates -->
    <template v-if="pageTab === 1">

      <!-- Add/Edit Date Dialog -->
      <v-dialog v-model="isDateModal" max-width="480" persistent content-class="eq-dialog">
        <v-card class="eq-dialog-card">
          <div class="eq-dialog-header">
            <h3 class="eq-dialog-title">
              {{ editingDate ? "Sanani tahrirlash" : "Yangi sana qo'shish" }}
            </h3>
            <button class="eq-close-btn" @click="isDateModal = false">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
          <div class="eq-dialog-body" style="padding: 20px 24px;">
            <div class="eq-field">
              <label class="eq-field__label">Sana</label>
              <DatePicker
                :value="dateForm.examDate"
                @input="dateForm.examDate = $event"
                label="Sanani tanlang"
              />
            </div>
            <div class="eq-form-row--two">
              <div class="eq-field">
                <label class="eq-field__label">Boshlanish vaqti</label>
                <input v-model="dateForm.startTime" class="eq-field__input" type="time" />
              </div>
              <div class="eq-field">
                <label class="eq-field__label">Tugash vaqti</label>
                <input v-model="dateForm.endTime" class="eq-field__input" type="time" />
              </div>
            </div>
            <div class="eq-field">
              <label class="eq-field__label">Maksimal ishtirokchilar soni</label>
              <input v-model.number="dateForm.maxParticipants" class="eq-field__input" type="number" />
            </div>
            <div class="eq-field">
              <label class="eq-toggle" :class="{ 'eq-toggle--active': dateForm.active }">
                <span class="eq-toggle__track" @click="dateForm.active = !dateForm.active">
                  <span class="eq-toggle__thumb" />
                </span>
                <span class="eq-toggle__text">Faol</span>
              </label>
            </div>
          </div>
          <div class="eq-dialog-footer">
            <button class="eq-btn eq-btn--ghost" @click="isDateModal = false">Bekor qilish</button>
            <button class="eq-btn eq-btn--primary" @click="saveDate">Saqlash</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- Delete Date Confirmation -->
      <v-dialog v-model="isDeleteDateModal" max-width="440" content-class="eq-dialog">
        <v-card class="eq-dialog-card">
          <div class="eq-dialog-header">
            <h3 class="eq-dialog-title">O'chirish</h3>
          </div>
          <div class="eq-dialog-body" style="padding: 20px 24px;">
            <p style="margin: 0; color: #344054; font-size: 14px;">Ushbu imtihon sanasini o'chirmoqchimisiz?</p>
          </div>
          <div class="eq-dialog-footer">
            <button class="eq-btn eq-btn--ghost" @click="isDeleteDateModal = false">Bekor qilish</button>
            <button class="eq-btn eq-btn--delete-confirm" @click="deleteDate">Ha, o'chirish</button>
          </div>
        </v-card>
      </v-dialog>

      <!-- Dates Table -->
      <div class="eq-list">
        <div class="eq-toolbar">
          <div class="eq-toolbar__spacer"></div>
          <v-btn
            class="add-btn"
            elevation="0"
            rounded
            color="#ffd451"
            @click="openAddDate"
          >
            <v-icon size="20" color="#1a1500">mdi-plus</v-icon>
          </v-btn>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :loading="examDatesLoading"
          :headers="[
            { text: '№', value: 'index', width: 60, sortable: false },
            { text: 'Sana', value: 'examDate', sortable: false },
            { text: 'Boshlanish', value: 'startTime', width: 120, sortable: false },
            { text: 'Tugash', value: 'endTime', width: 120, sortable: false },
            { text: 'Bo\'sh joylar', value: 'availableSlots', width: 140, sortable: false, align: 'center' },
            { text: '', value: 'actions', width: 120, sortable: false, align: 'center' },
          ]"
          :items="examDates"
          hide-default-footer
          class="eq-table"
        >
          <template v-slot:item.index="{ index }">
            {{ index + 1 }}
          </template>
          <template v-slot:item.examDate="{ item }">
            <span class="font-weight-medium">{{ formatExamDate(item.examDate) }}</span>
          </template>
          <template v-slot:item.startTime="{ item }">
            <span class="eq-chip">{{ item.startTime }}</span>
          </template>
          <template v-slot:item.endTime="{ item }">
            <span class="eq-chip">{{ item.endTime }}</span>
          </template>
          <template v-slot:item.availableSlots="{ item }">
            <span class="eq-badge">{{ item.availableSlots }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="row-actions">
              <button class="tc-act" @click="openEditDate(item)" :title="$t('Edit')">
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
              </button>
              <button class="tc-act tc-act--delete" @click="confirmDeleteDate(item.id)" :title="$t('Delete')">
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
              </button>
            </div>
          </template>
          <template v-slot:no-data>
            <empty-state :title="$t('No results found')" />
          </template>
        </v-data-table>
      </div>

    </template>

  </div>
</template>

<style lang="scss" scoped>
/* ==================================================
   EXAM QUESTIONS — Premium Admin Page
   ================================================== */

/* --- Page Tabs --- */
.eq-page-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: #f3f4f6;
  border-radius: 12px;
  margin-bottom: 20px;
  width: fit-content;
}

.eq-page-tab {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #667085;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #344054;
  }

  &--active {
    background: #fff;
    color: #1a1500;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  }
}

.eq {
  padding: 0 20px 20px;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display",
    "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif;
}

/* --- Form rows --- */
.eq-form-row {
  margin-bottom: 16px;
}

.eq-form-row--two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

/* --- Custom Fields --- */
.eq-field {
  margin-bottom: 16px;
}

.eq-field__label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-bottom: 6px;
}

.eq-field__input {
  display: block;
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.eq-field__input:focus {
  background: #fff;
  border-color: #ffd451;
  box-shadow: 0 0 0 3px rgba(255, 212, 81, 0.2);
}

/* --- Toggle Switch --- */
.eq-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.eq-toggle__track {
  position: relative;
  width: 40px;
  height: 22px;
  background: #d0d5dd;
  border-radius: 11px;
  transition: background 0.2s ease;
  display: inline-block;
}

.eq-toggle--active .eq-toggle__track {
  background: #ffd451;
}

.eq-toggle__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
  transition: transform 0.2s ease;
}

.eq-toggle--active .eq-toggle__thumb {
  transform: translateX(18px);
}

.eq-toggle__text {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
}

/* --- Total Counter --- */
.eq-total-row {
  margin-bottom: 16px;
}

.eq-total-card {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  padding: 14px 24px;
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 16px;
  box-shadow: 0 1px 0 rgba(2,6,23,.04);
}

.eq-total-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffd451, #f5c542);
}

.eq-total-count {
  font-size: 28px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.1;
  letter-spacing: -0.2px;
  font-variant-numeric: tabular-nums lining-nums;
}

.eq-total-label {
  font-size: 12px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: .08em;
}

/* --- Exam Config Card --- */
.eqc {
  position: relative;
  background: #fff;
  border: 1px solid #e6e8ec;
  border-radius: 16px;
  margin: 0 12px 16px;
  overflow: hidden;
}
.eqc__top {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 22px; border-bottom: 1px solid #f0f1f3;
  cursor: pointer; user-select: none; transition: background .15s;
}
.eqc__top:hover { background: #fafbfc; }
.eqc__title {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 700; color: #0f172a;
}
.eqc__title-icon { color: #94a3b8 !important; }
.eqc__chevron {
  color: #94a3b8 !important; transition: transform .2s ease;
}
.eqc__chevron--open { transform: rotate(180deg); }
.eqc__footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 22px; border-top: 1px solid #f0f1f3;
}
.eqc__cancel {
  display: inline-flex; align-items: center;
  padding: 6px 16px; font-size: 13px; font-weight: 600;
  border: 1px solid #d0d5dd; border-radius: 8px;
  background: #fff; color: #344054;
  cursor: pointer; transition: all .15s ease; font-family: inherit;
}
.eqc__cancel:hover { background: #f9fafb; border-color: #c0c7d0; }
.eqc__save {
  display: inline-flex; align-items: center;
  padding: 6px 16px; font-size: 13px; font-weight: 600;
  border: none; border-radius: 8px;
  background: #ffd451; color: #1a1500;
  cursor: pointer; transition: all .15s ease; font-family: inherit;
}
.eqc__save:hover { background: #f5c542; }
.eqc__save:disabled { opacity: .5; cursor: not-allowed; }
.eqc__grid {
  display: grid; grid-template-columns: repeat(5, 1fr); gap: 0;
  border-bottom: 1px solid #f0f1f3;
}
@media (max-width: 1100px) { .eqc__grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 700px) { .eqc__grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .eqc__grid { grid-template-columns: 1fr; } }
.eqc__item {
  position: relative; display: flex; flex-direction: column; gap: 6px;
  padding: 20px 22px; border-right: 1px solid #f0f1f3; transition: background .15s;
}
.eqc__item:last-child { border-right: none; }
.eqc__item--edit:hover { background: #f8fafc; }
.eqc__item--readonly { background: #fafbfc; }
.eqc__accent-bar {
  position: absolute; bottom: 0; left: 22px; right: 22px;
  height: 3px; border-radius: 3px 3px 0 0;
  background: var(--item-accent, #e5e7eb); opacity: .5; transition: opacity .2s;
}
.eqc__item:hover .eqc__accent-bar { opacity: 1; }
.eqc__label {
  display: block; font-size: 10px; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: .08em;
}
.eqc__input {
  width: 100%; font-size: 22px; font-weight: 800; color: #0f172a;
  border: none; background: transparent; outline: none;
  font-variant-numeric: tabular-nums; padding: 0;
  -moz-appearance: textfield; letter-spacing: -.02em;
}
.eqc__input::-webkit-outer-spin-button,
.eqc__input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.eqc__input:focus { color: var(--item-accent, #2563eb); }
.eqc__input-wrap { display: flex; align-items: baseline; gap: 6px; }
.eqc__unit { font-size: 13px; font-weight: 600; color: #cbd5e1; }
.eqc__value {
  font-size: 22px; font-weight: 800; color: #0f172a;
  font-variant-numeric: tabular-nums; letter-spacing: -.02em;
}
.eqc__value small { font-size: 13px; font-weight: 600; color: #cbd5e1; margin-left: 2px; }

/* ── Visibility Section ── */
.eqc__visibility { padding: 18px 22px 22px; }
.eqc__vis-header { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.eqc__vis-title { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .08em; }
.eqc__vis-badge {
  font-size: 10px; font-weight: 700; color: #067647;
  background: #dcfae6; border: 1px solid #abefc6;
  padding: 2px 8px; border-radius: 99px;
}
.eqc__type-grid { display: flex; flex-direction: column; gap: 8px; }
.eqc__type-card {
  border: 1px solid #f0f1f3; border-radius: 10px;
  overflow: hidden; transition: border-color .2s, box-shadow .2s;
}
.eqc__type-card--on { border-color: #d1fae5; box-shadow: 0 0 0 1px #d1fae5; }
.eqc__type-head {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; cursor: pointer; user-select: none; transition: background .15s;
}
.eqc__type-head:hover { background: #fafbfc; }
.eqc__type-name { font-size: 13px; font-weight: 600; color: #334155; }
.eqc__tick-box {
  width: 20px; height: 20px; border-radius: 6px; border: 2px solid #d0d5dd;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s; flex-shrink: 0;
}
.eqc__tick-box--on { background: #10b981; border-color: #10b981; }
.eqc__lang-list { display: flex; flex-wrap: wrap; gap: 6px; padding: 0 14px 12px 44px; }
.eqc__lang-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 12px; font-size: 12px; font-weight: 600;
  color: #64748b; background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 99px; cursor: pointer; user-select: none; transition: all .15s;
}
.eqc__lang-chip:hover { border-color: #cbd5e1; background: #f1f5f9; }
.eqc__lang-chip--on { color: #065f46; background: #d1fae5; border-color: #a7f3d0; }
.eqc__lang-chip--on:hover { background: #bbf7d0; }
.eqc__lang-check { color: #059669 !important; }
.eqc-expand-enter-active { transition: all .25s ease; }
.eqc-expand-leave-active { transition: all .15s ease; }
.eqc-expand-enter, .eqc-expand-leave-to { opacity: 0; max-height: 0; padding-bottom: 0 !important; }
.eqc-expand-enter-to, .eqc-expand-leave { opacity: 1; max-height: 100px; }

/* ── Dark Theme ── */
.theme--dark .eqc { background: #1a1b23; border-color: #2a2c3a; }
.theme--dark .eqc__top { border-color: #2a2c3a; }
.theme--dark .eqc__top:hover { background: #22232e; }
.theme--dark .eqc__title { color: #e4e5eb; }
.theme--dark .eqc__title-icon { color: #606275 !important; }
.theme--dark .eqc__chevron { color: #606275 !important; }
.theme--dark .eqc__footer { border-color: #2a2c3a; }
.theme--dark .eqc__cancel { background: #22232e; border-color: #33354a; color: #d5d6de; }
.theme--dark .eqc__cancel:hover { background: #2a2c3a; }
.theme--dark .eqc__save { background: #ffd451; color: #111318; }
.theme--dark .eqc__save:hover { background: #f5c542; }
.theme--dark .eqc__grid { border-color: #2a2c3a; }
.theme--dark .eqc__item { border-color: #2a2c3a; }
.theme--dark .eqc__item--edit:hover { background: #22232e; }
.theme--dark .eqc__item--readonly { background: #1a1b23; }
.theme--dark .eqc__label { color: #606275; }
.theme--dark .eqc__input { color: #e4e5eb; }
.theme--dark .eqc__input:focus { color: var(--item-accent, #60a5fa); }
.theme--dark .eqc__value { color: #e4e5eb; }
.theme--dark .eqc__unit { color: #606275; }
.theme--dark .eqc__accent-bar { opacity: .25; }
.theme--dark .eqc__item:hover .eqc__accent-bar { opacity: .7; }
.theme--dark .eqc__vis-title { color: #606275; }
.theme--dark .eqc__vis-badge { color: #86efac; background: rgba(6,118,71,.15); border-color: rgba(6,118,71,.3); }
.theme--dark .eqc__type-card { border-color: #2a2c3a; }
.theme--dark .eqc__type-card--on { border-color: rgba(6,118,71,.4); box-shadow: none; }
.theme--dark .eqc__type-head:hover { background: #22232e; }
.theme--dark .eqc__type-name { color: #d5d6de; }
.theme--dark .eqc__tick-box { border-color: #33354a; }
.theme--dark .eqc__tick-box--on { background: #16a34a; border-color: #16a34a; }
.theme--dark .eqc__lang-chip { color: #8b8d9e; background: #22232e; border-color: #2a2c3a; }
.theme--dark .eqc__lang-chip:hover { background: #2a2c3a; border-color: #33354a; }
.theme--dark .eqc__lang-chip--on { color: #86efac; background: rgba(6,118,71,.15); border-color: rgba(6,118,71,.3); }
.theme--dark .eqc__lang-chip--on:hover { background: rgba(6,118,71,.25); }
.theme--dark .eqc__lang-check { color: #86efac !important; }

/* --- Stats Grid --- */
.eq-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

@media (max-width: 1280px) {
  .eq-stats-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 960px) {
  .eq-stats-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 560px) {
  .eq-stats-grid { grid-template-columns: 1fr; }
}

.eq-stat-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 14px;
  padding: 16px;
  min-height: 92px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;

  transition: border-color .18s ease, transform .18s ease, background-color .18s ease;
  outline: none;

  /* top stripe removed per user request */

  /* subtle interactions */
  &:hover  { transform: translateY(-1px); border-color: #d7d9df; }
  &:active { transform: translateY(0); }
  &:focus  { outline: none; }

  /* hover wave */
  &::after {
    content: "";
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(0,0,0,.03), transparent);
    transform: translateX(-100%); pointer-events: none;
  }
  &:hover::after { animation: wave 1200ms ease; }

  /* initial mount reveal */
  animation: slideUpFade .38s ease both;
}
.eq-stat-card:nth-child(1){ animation-delay: 40ms; }
.eq-stat-card:nth-child(2){ animation-delay: 80ms; }
.eq-stat-card:nth-child(3){ animation-delay: 120ms; }
.eq-stat-card:nth-child(4){ animation-delay: 160ms; }

.eq-stat-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.eq-stat-card__title {
  color: #667085;
  font-size: 12px; line-height: 1.2;
  letter-spacing: .08em;
  text-transform: uppercase;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  flex: 1;
  min-width: 0;
}

.eq-stat-card__lang {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  color: #067647;
  background: #dcfae6;
  border: 1px solid #abefc6;
  border-radius: 999px;
  white-space: nowrap;
}

.eq-stat-card__count {
  color: #0f172a;
  font-weight: 900;
  font-size: clamp(24px, 3.6vw, 36px);
  line-height: 1.05;
  letter-spacing: -0.2px;
  font-variant-numeric: tabular-nums lining-nums;
  text-align: right;
}

.eq-stat-card--active {
  outline: 3px solid #e6e8eb;
}

/* --- List Card --- */
.eq-list {
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 16px;
  box-shadow: 0 1px 0 rgba(2,6,23,.04);
  overflow-x: auto;
}

/* --- Toolbar --- */
.eq-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  flex-wrap: wrap;
  border-bottom: 1px solid #f1f3f5;
}

.eq-toolbar__title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  letter-spacing: -0.2px;
}

.eq-toolbar__spacer { flex: 1; }

/* Search-by-content input */
.eq-search {
  max-width: 360px;
  ::v-deep .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 40px !important;
  }
  ::v-deep fieldset { border-color: transparent !important; }
  ::v-deep .v-input--is-focused fieldset { border-color: #00274c !important; border-width: 1.5px !important; }
  ::v-deep .v-icon { color: #94a3b8 !important; }
  ::v-deep input { font-size: 14px; }
  ::v-deep input::placeholder { color: #94a3b8 !important; }
}
.theme--dark .eq-search {
  ::v-deep .v-input__slot { background: #22232e !important; }
  ::v-deep .v-input--is-focused fieldset { border-color: #ffd451 !important; }
  ::v-deep .v-icon { color: #606275 !important; }
  ::v-deep input { color: #d5d6de !important; }
  ::v-deep input::placeholder { color: #606275 !important; }
}

.filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5 !important;
  color: #f5f5f5;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  border: 1px solid #d0d5dd;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.filter-btn img {
  width: 18px;
  height: 18px;
  object-fit: cover;
  margin-right: 0 !important;
}

.filter-btn:hover {
  background-color: #e8e8e8 !important;
}

.filter-btn:active {
  background-color: #ddd !important;
  transform: scale(0.97);
}

.filter-btn--active {
  background-color: #e8e8e8 !important;
  border-color: #ccc;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  background-color: #ffd451 !important;
  border: 1px solid #f5c542;
  cursor: pointer;
  transition: all 0.2s ease;
}
.add-btn:hover {
  background-color: #f5c542 !important;
}
.add-btn:active {
  background-color: #e6b432 !important;
  transform: scale(0.97);
}

.excel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5 !important;
  color: #344054;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  border: 1px solid #d0d5dd;
  cursor: pointer;
  transition: all 0.2s ease;
}
.excel-btn img {
  width: 18px;
  height: 18px;
  object-fit: cover;
  margin-right: 0 !important;
}
.excel-btn:hover {
  background-color: #e8e8e8 !important;
}
.excel-btn:active {
  background-color: #ddd !important;
  transform: scale(0.97);
}

.edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  background-color: #7c3aed !important;
  border: 1px solid #6d28d9;
  cursor: pointer;
  transition: all 0.2s ease;
}
.edit-btn:hover {
  background-color: #6d28d9 !important;
}
.edit-btn:active {
  background-color: #5b21b6 !important;
  transform: scale(0.97);
}

.delete-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  background-color: #dc2626 !important;
  border: 1px solid #b91c1c;
  cursor: pointer;
  transition: all 0.2s ease;
}
.delete-btn:hover {
  background-color: #b91c1c !important;
}
.delete-btn:active {
  background-color: #991b1b !important;
  transform: scale(0.97);
}
.delete-badge {
  position: absolute;
  top: -12px;
  right: -3px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 7px;
  background: #dc2626;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
}

.eq-filter-badge {
  position: absolute;
  top: -12px;
  right: -3px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 7px;
  background: #dc2626;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  z-index: 1;
}

.button-primary {
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 10px;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  text-transform: none;
}

.button-primary:hover {
  background-color: #f9fafb;
  border-color: #c0c7d0;
}

.button-primary:active {
  background-color: #f0f2f5;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* --- Buttons (matching qabul button-primary style) --- */
.eq-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #344054;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  white-space: nowrap;
  outline: none;
  font-family: inherit;
  text-transform: none;

  &:hover {
    background: #f9fafb;
    border-color: #c0c7d0;
  }

  &:active {
    background: #f0f2f5;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.eq-btn--primary {
  background: #ffd451;
  border-color: #f5c542;
  color: #1a1500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) { background: #f5c542; }
  &:active:not(:disabled) { background: #e6b432; transform: scale(0.97); }
}

.eq-btn--excel {
  background: #148b4b;
  color: #fff;
  border-color: #148b4b;

  &:hover:not(:disabled) { background: #117a40; }
  &:active:not(:disabled) { background: #0d6b35; transform: scale(0.97); }
}

.eq-btn--delete-confirm {
  background: #dc2626;
  color: #fff;
  border-color: #b91c1c;

  &:hover:not(:disabled) { background: #b91c1c; }
  &:active:not(:disabled) { background: #991b1b; transform: scale(0.97); }
}

.eq-btn--danger {
  background: transparent;
  color: #344054;
  border: 1px solid #d0d5dd;

  &:hover:not(:disabled) { background: #f9fafb; border-color: #c0c7d0; }
  &:active:not(:disabled) { background: #f0f2f5; }
}

.eq-btn--ghost {
  background: #fff;
  color: #344054;
  border: 1px solid #d0d5dd;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);

  &:hover { background: #f9fafb; border-color: #c0c7d0; }
  &:active { background: #f0f2f5; box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05); }
}

.eq-icon-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #344054;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  outline: none;

  &:hover { background: #f9fafb; border-color: #c0c7d0; }
  &:active { background: #f0f2f5; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

/* --- Checkbox columns --- */
.eq-th-check,
.eq-td-check {
  width: 50px !important;
  padding: 8px 12px !important;
  text-align: center;
}

/* --- Table --- */
.eq-table {
  border-radius: 0 !important;
}

.eq-table ::v-deep table {
  min-width: 900px;
}

.eq-thead th {
  font-size: 11px !important;
  font-weight: 800 !important;
  text-transform: uppercase !important;
  letter-spacing: .06em !important;
  color: #667085 !important;
  background: #f9fafb !important;
  border-bottom: 1px solid #e6e8eb !important;
  padding: 12px 16px !important;
  white-space: nowrap;
}

.eq-row {
  transition: background .12s ease;

  &:hover {
    background: #f8fafc !important;
  }

  td {
    padding: 12px 16px !important;
    border-bottom: 1px solid #f1f3f5 !important;
    font-size: 13px;
  }
}

.eq-row--selected {
  background: rgba(255, 212, 81, 0.1) !important;
}
.eq-row--clickable { cursor: pointer; }

/* ── Read-only question preview ── */
.eq-preview__meta { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.eq-preview__body {
  font-size: 15px; font-weight: 600; line-height: 1.5; color: #0f172a;
  white-space: pre-wrap; word-break: break-word; margin-bottom: 16px;
}
.eq-preview__img {
  max-width: 100%; max-height: 260px; object-fit: contain; display: block;
  border-radius: 10px; border: 1px solid #e6e8ec; margin-bottom: 16px;
}
.eq-preview__answers { display: flex; flex-direction: column; gap: 8px; }
.eq-preview__answer {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border: 1px solid #e6e8ec; border-radius: 10px;
  font-size: 14px; color: #0f172a; background: #fff;
}
.eq-preview__answer.is-correct { border-color: #148b4b; background: rgba(20, 139, 75, .07); font-weight: 600; }
.eq-preview__letter {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 6px; flex-shrink: 0;
  background: #f1f5f9; color: #475569; font-size: 12px; font-weight: 800;
}
.eq-preview__answer.is-correct .eq-preview__letter { background: #148b4b; color: #fff; }
.eq-preview__value { flex: 1; word-break: break-word; }
.eq-preview__tick { margin-left: auto; }

.theme--dark .eq-preview__body { color: #e4e5eb; }
.theme--dark .eq-preview__img { border-color: #2a2c3a; }
.theme--dark .eq-preview__answer { background: #1a1b23; border-color: #2a2c3a; color: #e4e5eb; }
.theme--dark .eq-preview__answer.is-correct { border-color: #148b4b; background: rgba(20, 139, 75, .16); }
.theme--dark .eq-preview__letter { background: #22232e; color: #8a8fa3; }

.eq-row__num {
  font-weight: 700;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}

.eq-row__body {
  color: #1e293b;
  font-weight: 500;
  line-height: 1.5;
  max-width: 400px;
}

.eq-chip {
  display: inline-block;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
  background: #f9fafb;
  border: 1px solid #d0d5dd;
  border-radius: 16px;
  white-space: nowrap;
}

.eq-chip--type {
  color: #6941c6;
  background: #f9f5ff;
  border-color: #e9d7fe;
}

.eq-chip--science {
  color: #b54708;
  background: #fffaeb;
  border-color: #fedf89;
}

.eq-chip--empty {
  color: #98a2b3;
  background: none;
  border: none;
}

.eq-chip--lang {
  color: #067647;
  background: #ecfdf3;
  border-color: #abefc6;
}

.eq-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 800;
  color: #1a1500;
  background: linear-gradient(135deg, #ffd451, #f5c542);
  border-radius: 8px;
}

.eq-action-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #667085;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  outline: none;
  margin-right: 4px;

  &:hover { background: #f9fafb; color: #344054; border-color: #c0c7d0; }
  &:active { background: #f0f2f5; }
}

.eq-action-btn--danger {
  &:hover { background: #fef2f2; color: #dc2626; border-color: #fca5a5; }
  &:active { background: #fee2e2; }
}

.eq-empty {
  text-align: center;
  padding: 48px 16px !important;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
}

/* --- Pagination --- */
.eq-pagination {
  display: flex;
  justify-content: center;
  padding: 16px 20px;
  border-top: 1px solid #f1f3f5;

  &::v-deep .v-pagination__item {
    border: 1px solid #d0d5dd;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    font-weight: 500;
    font-size: 14px;
    border-radius: 8px;
  }
  &::v-deep .v-pagination__item--active {
    background: #ffd451 !important;
    border-color: #f5c542 !important;
    color: #1a1500 !important;
  }
  &::v-deep .v-pagination__navigation {
    border: 1px solid #d0d5dd;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    border-radius: 8px;
  }
}

/* --- Dialog --- */
.eq-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,.18), 0 0 0 1px rgba(0,0,0,.04) !important;
}

.eq-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #e6e8eb;
  background: #fafbfc;
}

/* Segmented tab bar */
.eq-seg-wrap {
  padding: 12px 24px 0;
}
.eq-seg {
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  background: #f1f5f9;
  border: 1px solid #e6e8eb;
  border-radius: 999px;
  padding: 2px;
  height: 40px;
  align-items: center;
  overflow: hidden;
}
.eq-seg__btn {
  z-index: 2;
  appearance: none;
  border: 0;
  background: transparent;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
  text-align: center;
}
.eq-seg__btn.is-active {
  color: #000;
}
.eq-seg__fill {
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 2px;
  width: calc(50% - 4px);
  border-radius: 999px;
  background: #ffd451;
  transition: transform .18s ease;
}
.eq-seg__icon {
  width: 14px;
  height: 14px;
  margin-right: 4px;
  vertical-align: middle;
  filter: brightness(0);
}

.eq-dialog-title {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  letter-spacing: -.01em;
}

.eq-close-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  transition: all .15s ease;
  outline: none;

  &:hover { background: #f3f4f6; border-color: #d0d5dd; }
}

.eq-dialog-body {
  padding: 24px;
}

.eq-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid #e6e8eb;
  background: #fafbfc;
}

/* --- Answer Rows --- */
.eq-answers-label {
  font-size: 13px;
  font-weight: 700;
  color: #344054;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.eq-answer-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid #e6e8eb;
  background: #fff;
  transition: all .15s ease;
}

.eq-answer-row--correct {
  border-color: #fde68a;
  background: #fffbeb;
}

.eq-answer-radio {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all .15s ease;
  outline: none;

  &:hover { border-color: #f5c542; }
}

.eq-answer-radio--active {
  background: #ffd451;
  border-color: #f5c542;
}

.eq-answer-letter {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  flex-shrink: 0;
}

.eq-answer-input {
  flex: 1;
}

.eq-answer-hint {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}

/* --- Question Image --- */
.eq-image-section { }
.eq-image-preview {
  position: relative;
  display: inline-block;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e0e3eb;
}
.eq-image-preview__img {
  max-width: 100%;
  max-height: 180px;
  object-fit: contain;
  display: block;
}
.eq-image-preview__remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0,0,0,0.5);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.eq-image-preview__remove:hover { background: rgba(220,38,38,0.8); }

/* --- Upload Format Hint --- */
.eq-format-hint {
  padding: 14px 16px;
  border-radius: 12px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  margin-bottom: 16px;
}

.eq-format-hint__title {
  font-size: 12px;
  font-weight: 700;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 6px;
}

.eq-format-hint__body {
  font-size: 13px;
  font-weight: 500;
  color: #0c4a6e;
}

.eq-format-sep {
  color: #93c5fd;
  margin: 0 4px;
}

.eq-format-hint__note {
  font-size: 11px;
  color: #7dd3fc;
  margin-top: 6px;
  font-weight: 600;
}

.eq-upload-result {
  display: flex;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  margin-top: 16px;
}

.eq-upload-result--success {
  background: #f0fdf4;
  border: 1px solid #86efac;
  color: #166534;
}

.eq-upload-result--warn {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.eq-upload-errors {
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.8;
}

/* --- Animation --- */
@keyframes wave {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
@keyframes slideUpFade {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.eq-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ==================================================
   DARK MODE
   ================================================== */
.eq--dark {
  .eq-total-card {
    background: #1a1b23;
    border-color: #2a2c3a;
  }
  .eq-total-count { color: #e4e5eb; }
  .eq-total-label { color: #8b8d9e; }
  .eq-total-icon { background: linear-gradient(135deg, #ffd451, #f5c542); }

  .eq-stat-card {
    background: #1a1b23;
    border-color: #2a2c3a;

    &:hover {
      border-color: #33354a;
      transform: translateY(-1px);
    }
  }
  .eq-stat-card--active {
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 25%, transparent) !important;
  }
  .eq-stat-card__title { color: #8b8d9e; }
  .eq-stat-card__count { color: #e4e5eb; }
  .eq-stat-card__lang { background: rgba(6,118,71,.15); border-color: rgba(6,118,71,.3); color: #86efac; }

  .eq-list {
    background: #1a1b23;
    border-color: #2a2c3a;
  }

  .eq-toolbar {
    border-bottom-color: #2a2c3a;
  }
  .eq-toolbar__title { color: #e4e5eb; }

  .filter-btn {
    background-color: #22232e !important;
    border-color: #2a2c3a;
  }
  .filter-btn:hover {
    background-color: #33354a !important;
  }
  .filter-btn--active {
    background-color: #33354a !important;
    border-color: #3a3c4e;
  }

  .add-btn {
    background-color: #ffd451 !important;
    border-color: #f5c542;
  }
  ::v-deep .add-btn.v-btn,
  ::v-deep .add-btn.v-btn.v-btn--has-bg,
  ::v-deep .add-btn.theme--dark.v-btn,
  ::v-deep .add-btn.theme--dark.v-btn.v-btn--has-bg {
    background-color: #ffd451 !important;
  }
  .add-btn:hover,
  ::v-deep .add-btn.v-btn:hover,
  ::v-deep .add-btn.theme--dark.v-btn:hover {
    background-color: #f5c542 !important;
  }

  .excel-btn {
    background-color: #22232e !important;
    border-color: #2a2c3a;
  }
  .excel-btn:hover {
    background-color: #33354a !important;
  }

  .edit-btn {
    background-color: #7c3aed !important;
    border-color: #6d28d9;
  }
  .edit-btn:hover {
    background-color: #6d28d9 !important;
  }

  .delete-btn {
    background-color: #dc2626 !important;
    border-color: #b91c1c;
  }
  .delete-btn:hover {
    background-color: #b91c1c !important;
  }

  .button-primary {
    background-color: #1a1b23;
    border-color: #2a2c3a;
    color: #e4e5eb;
    box-shadow: none;
  }
  .button-primary:hover {
    background-color: #22232e;
    border-color: #33354a;
  }

  .eq-thead th {
    background: #111318 !important;
    color: #8b8d9e !important;
    border-bottom-color: #2a2c3a !important;
  }

  .eq-row {
    &:hover { background: #22232e !important; }
    td { border-bottom-color: #22232e !important; }
  }
  .eq-row--selected {
    background: rgba(255, 212, 81, 0.08) !important;
  }
  .eq-row__num { color: #606275; }
  .eq-row__body { color: #e4e5eb; }

  .eq-chip {
    background: #22232e;
    border-color: #2a2c3a;
    color: #b0b2be;
  }

  .eq-chip--type {
    background: rgba(105, 65, 198, 0.1);
    border-color: rgba(105, 65, 198, 0.25);
    color: #b692f6;
  }

  .eq-chip--science {
    background: rgba(181, 71, 8, 0.1);
    border-color: rgba(181, 71, 8, 0.25);
    color: #fec84b;
  }

  .eq-chip--lang {
    background: rgba(6, 118, 71, 0.1);
    border-color: rgba(6, 118, 71, 0.25);
    color: #75e0a7;
  }

  .eq-badge {
    background: linear-gradient(135deg, #ffd451, #f5c542);
    color: #1a1500;
  }

  .eq-action-btn {
    background: #1a1b23;
    border-color: #2a2c3a;
    color: #8b8d9e;
    box-shadow: none;

    &:hover { background: #22232e; color: #e4e5eb; border-color: #33354a; }
  }
  .eq-action-btn--danger:hover {
    background: rgba(220, 38, 38, 0.1);
    color: #f87171;
    border-color: rgba(220, 38, 38, 0.3);
  }

  .eq-empty { color: #606275; }

  .eq-pagination {
    border-top-color: #2a2c3a;

    &::v-deep .v-pagination__item {
      background: #1a1b23 !important;
      border-color: #2a2c3a;
      color: #e4e5eb;
      box-shadow: none;
    }
    &::v-deep .v-pagination__item--active {
      background: #ffd451 !important;
      border-color: #ffd451 !important;
      color: #1a1500 !important;
    }
    &::v-deep .v-pagination__navigation {
      background: #1a1b23 !important;
      border-color: #2a2c3a;
      box-shadow: none;

      .v-icon { color: #8b8d9e; }
    }
  }

  .eq-btn {
    background: #1a1b23;
    border-color: #2a2c3a;
    color: #e4e5eb;
    box-shadow: none;

    &:hover { background: #22232e; border-color: #33354a; }
    &:active { background: #2a2c3a; }
  }

  .eq-icon-btn {
    background: #1a1b23;
    border-color: #2a2c3a;
    color: #8b8d9e;
    box-shadow: none;

    &:hover { background: #22232e; color: #e4e5eb; border-color: #33354a; }
  }

  .eq-btn--ghost {
    background: #1a1b23;
    border-color: #2a2c3a;
    color: #e4e5eb;
    box-shadow: none;

    &:hover { background: #22232e; border-color: #33354a; }
  }

  .eq-btn--danger {
    background: transparent;
    color: #e4e5eb;
    border-color: #2a2c3a;

    &:hover:not(:disabled) { background: #22232e; }
  }

  .eq-btn--primary {
    background: #ffd451;
    border-color: #ffd451;
    color: #1a1500;

    &:hover:not(:disabled) { background: #f5c542; }
  }

  .eq-btn--excel {
    background: #148b4b;
    border-color: #148b4b;
    color: #fff;
  }

  /* --- Page Tabs --- */
  .eq-page-tabs {
    background: #22232e;
  }
  .eq-page-tab {
    color: #8b8d9e;
    &:hover { color: #e4e5eb; }
    &--active {
      background: #1a1b23;
      color: #e4e5eb;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    }
  }

  /* --- Exam Dates table --- */
  .eq-table {
    background: #1a1b23 !important;
  }
  ::v-deep .eq-table .v-data-table__wrapper {
    background: #1a1b23 !important;
  }
  ::v-deep .eq-table thead th {
    background: #1a1b23 !important;
    color: #8b8d9e !important;
    border-bottom-color: #2a2c3a !important;
  }
  ::v-deep .eq-table tbody td {
    color: #e4e5eb !important;
    border-bottom-color: #22232e !important;
  }
  ::v-deep .eq-table tbody tr:hover {
    background: #22232e !important;
  }

  /* Dialog dark styles moved to unscoped block below (v-dialog teleports to body) */
}

/* --- Row Actions (promotion-style) --- */
.row-actions { display: flex; gap: 6px; justify-content: flex-end; }
.tc-act {
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: none; color: #475569; cursor: pointer; transition: all .15s;
}
.tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.06); }
.tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.06); }

/* dark mode */
.theme--dark .tc-act { border-color: #2a2c3a; color: #8b8d9e; }
.theme--dark .tc-act:hover { border-color: #ffd451; color: #ffd451; background: rgba(255,212,81,.08); }
.theme--dark .tc-act--delete:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,.08); }

/* --- Reduced Motion --- */
@media (prefers-reduced-motion: reduce) {
  .eq-stat-card { transition: none; animation: none; }
}
@media (max-width: 600px) {
  .eq-form-row--two { grid-template-columns: 1fr; }
}
</style>

<style lang="scss">
/* Dialog dark — unscoped because v-dialog teleports content to body */
.theme--dark .eq-dialog .eq-dialog-card {
  background: #1a1b23 !important;
  box-shadow: 0 20px 60px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.04) !important;
}
.theme--dark .eq-dialog .eq-dialog-header { border-bottom-color: #2a2c3a; background: #16171e; }
.theme--dark .eq-dialog .eq-dialog-title { color: #e4e5eb; }
.theme--dark .eq-dialog .eq-dialog-footer { border-top-color: #2a2c3a; background: #16171e; }
.theme--dark .eq-dialog .eq-seg { background: #22232e; border-color: #2a2c3a; }
.theme--dark .eq-dialog .eq-seg__btn { color: #9ca3af; }
.theme--dark .eq-dialog .eq-seg__btn:not(.is-active) .eq-seg__icon {
  filter: brightness(0) invert(1);
}
.theme--dark .eq-dialog .eq-seg__btn.is-active { color: #000; }
.theme--dark .eq-dialog .eq-seg__btn.is-active .v-icon { color: #000 !important; }
.theme--dark .eq-dialog .eq-close-btn {
  background: #22232e;
  border-color: #2a2c3a;
  &:hover { background: #33354a; }
}
.theme--dark .eq-dialog .eq-answer-row {
  background: #1a1b23;
  border-color: #2a2c3a;
}
.theme--dark .eq-dialog .eq-answer-row--correct {
  background: rgba(255, 212, 81, 0.08);
  border-color: rgba(255, 212, 81, 0.3);
}
.theme--dark .eq-dialog .eq-answer-letter { background: #22232e; color: #b0b2be; }
.theme--dark .eq-dialog .eq-answer-radio { border-color: #606275; background: #22232e; }
.theme--dark .eq-dialog .eq-answer-radio--active { background: #ffd451; border-color: #f5c542; }
.theme--dark .eq-dialog .eq-answers-label { color: #b0b2be; }
.theme--dark .eq-dialog .eq-answer-hint { color: #606275; }
.theme--dark .eq-dialog .eq-format-hint {
  background: rgba(3, 105, 161, 0.08);
  border-color: rgba(3, 105, 161, 0.2);
}
.theme--dark .eq-dialog .eq-format-hint__title { color: #38bdf8; }
.theme--dark .eq-dialog .eq-format-hint__body { color: #7dd3fc; }
.theme--dark .eq-dialog .eq-format-hint__note { color: #606275; }
.theme--dark .eq-dialog .eq-format-sep { color: #33354a; }
.theme--dark .eq-dialog .eq-upload-result--success {
  background: rgba(20, 139, 75, 0.08);
  border-color: rgba(20, 139, 75, 0.2);
  color: #86efac;
}
.theme--dark .eq-dialog .eq-upload-result--warn {
  background: rgba(184, 131, 0, 0.08);
  border-color: rgba(184, 131, 0, 0.2);
  color: #fde68a;
}

/* Also style inputs/selects inside the dialog for dark mode */
.theme--dark .eq-dialog .v-input .v-input__slot {
  background: #22232e !important;
}
.theme--dark .eq-dialog .v-text-field > .v-input__control > .v-input__slot::before {
  border-color: #2a2c3a !important;
}
.theme--dark .eq-dialog .v-label { color: #8b8d9e !important; }
.theme--dark .eq-dialog .v-select__selections,
.theme--dark .eq-dialog .v-text-field input,
.theme--dark .eq-dialog .v-text-field textarea {
  color: #e4e5eb !important;
}
.theme--dark .eq-dialog .eq-btn--ghost {
  border-color: #606275;
  color: #e4e5eb;
  background: #22232e;
  &:hover { background: #33354a; }
  .v-icon { color: #e4e5eb !important; }
}
.theme--dark .eq-dialog .eq-btn--primary {
  background: #ffd451;
  color: #000;
  border-color: #ffd451;
  .v-icon { color: #000 !important; }
}
.theme--dark .eq-dialog .eq-dialog-body p {
  color: #d5d6de !important;
}
.theme--dark .eq-dialog .eq-field__label {
  color: #8b8d9e;
}
.theme--dark .eq-dialog .eq-field__input {
  background: #22232e;
  color: #e4e5eb;
  border-color: #3a3c4e;
}
.theme--dark .eq-dialog .eq-field__input:focus {
  border-color: #ffd451;
  box-shadow: 0 0 0 3px rgba(255, 212, 81, 0.15);
}
.theme--dark .eq-dialog .eq-toggle__track {
  background: #3a3c4e;
}
.theme--dark .eq-dialog .eq-toggle--active .eq-toggle__track {
  background: #ffd451;
}
.theme--dark .eq-dialog .eq-toggle__text {
  color: #d5d6de;
}

/* Limit autocomplete dropdown height inside dialogs */
.eq-faculty-menu.v-menu__content {
  max-height: 200px !important;
  overflow-y: auto !important;
}
</style>
